import { useCallback, useEffect, useRef } from 'react';
import { StyleSheet } from 'react-native';
import Animated, { runOnJS } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useNavigation } from '@react-navigation/native';
import { useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp, NativeStackScreenProps } from '@react-navigation/native-stack';

import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import BetrAnalytics from '@/feature/analytics/analytics';
import { CircularPager } from '@/feature/circular-pager/CircularPager';
import { usePagerSmoothCloseOnRemoval } from '@/feature/circular-pager/hooks/use-pager-smooth-close-on-removal';
import { CircularPagerImperativeApi, CircularPagerPageProps } from '@/feature/circular-pager/types';
import { useResponsiveDimensions } from '@/feature/responsive-design/hooks/use-responsive-dimensions';
import { useEventsTabsStore } from '@/hooks/use-events-tabs-store';
import { RootStackParamList } from '@/navigation/types';
import { common, designSystem } from '@/styles/styles';
import { isWeb } from '@/utils/constants-platform-specific';

import { Handle } from '../alerts/components/Modal';
import { AnalyticsEvent } from '../analytics/constants';
import { SHEET_BORDER_RADIUS } from '../circular-pager/CircularPageSlot';
import {
    FullScreenPagerTransitionProvider,
    useFullScreenPagerTransition,
} from '../circular-pager/FullScreenPagerTransitionProvider';
import { SheetBackground } from '../circular-pager/SheetBackground';
import { getRealIndex, moddedIndex, moveToClosestIndex } from '../circular-pager/circular-helpers';
import { EmptyModal } from '../fantasy-event-sheets/components/EventDetailsSheetScreen';
import { EVENT_CAROUSEL_HEIGHT } from '../fantasy-event-sheets/components/EventsCarouselTabBar';
import { useEvents } from '../league-hub-sbk/hooks/use-events';
import { useSubscribeLeagueMatchUpdate } from '../league-hub-sbk/hooks/use-subscribe-league-match-update';
import { MaxWidthWrapper } from '../responsive-design/WebComponents';
import { SbkEventDetailsSheetScreen } from './SbkEventDetailsSheetScreen';
import { SbkEventsCarouselTabBar } from './components/SbkEventsCarouselTabBar';

export type SbkEventsSheetsParamsList = NativeStackScreenProps<RootStackParamList, 'SbkEventsSheets'>;

export const SbkEventsSheetsScreen = () => {
    const { params } = useRoute<SbkEventsSheetsParamsList['route']>();
    useSubscribeLeagueMatchUpdate(params.leagueId);
    usePagerSmoothCloseOnRemoval();

    const selectedTab = useEventsTabsStore(state => state.selectedTab);

    return (
        <FullScreenPagerTransitionProvider key={params.contextKey} initialIndex={selectedTab}>
            <SbkEventsSheetsTabs />
        </FullScreenPagerTransitionProvider>
    );
};

const PAGE_GAP = 20;

const SbkEventsSheetsTabs = () => {
    const { params } = useRoute<SbkEventsSheetsParamsList['route']>();

    const { height, width } = useResponsiveDimensions();

    const { data: events, isFetched } = useEvents({ leagueId: params.leagueId });

    const selectedTab = useEventsTabsStore(state => state.selectedTab);
    const setSelectedTab = useEventsTabsStore(state => state.actions.setSelectedTab);

    const noData = isFetched && events?.length === 0;

    const totalPages = events?.length || 0;

    const internalTab = useRef(selectedTab);

    const validInitialTab = selectedTab;

    const { selectedTabSharedValue, pageAnim } = useFullScreenPagerTransition();

    const pagerRef = useRef<CircularPagerImperativeApi>(null);

    // if the store's selectedTab changes from outside, jump the pager
    useEffect(() => {
        if (internalTab.current !== selectedTab && totalPages > 0) {
            internalTab.current = selectedTab;
            selectedTabSharedValue.value = selectedTab;
            pagerRef.current?.setPage(moveToClosestIndex(pageAnim.value, selectedTab, totalPages), { animated: true });
        }
    }, [selectedTab, totalPages, selectedTabSharedValue, pageAnim]);

    // Initialize selected tab from eventId param.
    // Uses animated: false so the pager jumps instantly, preventing the mount-time
    // useAnimatedReaction(onPageChange(0)) from racing with the correct index.
    const hasInitialized = useRef(false);
    useEffect(() => {
        if (hasInitialized.current) {
            return;
        }
        if (params.eventId && events && events.length > 1) {
            const found = events.findIndex(event => event.id === params.eventId);
            if (found !== -1) {
                hasInitialized.current = true;
                internalTab.current = found;
                selectedTabSharedValue.value = found;
                setSelectedTab(found);
                pagerRef.current?.setPage(found, { animated: false });
            }
        }
    }, [params.eventId, setSelectedTab, events, selectedTabSharedValue]);

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList, 'SbkEventsSheets'>>();

    const onClose = useCallback(() => {
        navigation.goBack();
    }, [navigation]);

    const trackPageSwipe = useCallback(() => {
        BetrAnalytics.trackEvent(AnalyticsEvent.SBK_PAGER_EVENT_SWIPE);
    }, []);

    const handlePageChange = useCallback(
        (rawIndex: number) => {
            const realIndex = totalPages === 0 ? rawIndex : moddedIndex(rawIndex, totalPages);
            runOnJS(() => {
                selectedTabSharedValue.value = realIndex;
                internalTab.current = realIndex;
                setSelectedTab(realIndex);
            })();
        },
        [setSelectedTab, selectedTabSharedValue, totalPages]
    );

    const insets = useSafeAreaInsets();

    const onlyOnePage = events?.length === 1;

    const headerHeight = onlyOnePage ? 0 : EVENT_CAROUSEL_HEIGHT;

    const renderPage = useCallback(
        ({ offset, shouldFreeze, isActive, currentIndex }: CircularPagerPageProps) => {
            const realIndex = getRealIndex(offset, currentIndex, events?.length || 0);
            const event = events?.[realIndex];

            if (!event) {
                // If the modal is opened before any events are loaded, display an empty modal.
                // This can occur, for example, when the user launches it from a player profile sheet from lobby
                // or selects a league that has only one event.
                return <EmptyModal />;
            }

            return <SbkEventDetailsSheetScreen shouldFreeze={shouldFreeze} event={event} isActive={isActive} />;
        },
        [events]
    );

    const positionStyle = isWeb ? undefined : common.absolute;

    return (
        <Box width={width} height={'100%'}>
            <SheetBackground />
            {onlyOnePage ? null : (
                <MaxWidthWrapper>
                    <SbkEventsCarouselTabBar />
                </MaxWidthWrapper>
            )}
            <Animated.View
                style={[
                    {
                        height,
                        top: insets.top,
                        width,
                        backgroundColor: isWeb ? designSystem.colors.neutral900 : undefined,
                    },
                    positionStyle,
                ]}
            >
                <CircularPager
                    ref={pagerRef}
                    pageCallbackNode={pageAnim}
                    isEmpty={noData}
                    renderEmptyView={NoEvents}
                    initialIndex={validInitialTab}
                    onPageChange={handlePageChange}
                    pageGap={PAGE_GAP}
                    renderPage={renderPage}
                    aboveSheetHeaderHeight={headerHeight}
                    onClose={onClose}
                    onPageSwipe={trackPageSwipe}
                    onlyOnePage={onlyOnePage}
                />
                <Box
                    style={{
                        transform: [{ translateY: height }],
                        width,
                        height,
                    }}
                    zIndex={-1}
                    backgroundColor={ds.colorToken.background.neutral.base}
                />
            </Animated.View>
        </Box>
    );
};

export const NoEvents = () => {
    return (
        <Box
            paddingHorizontal={'s24'}
            flex={1}
            backgroundColor={ds.colorToken.background.neutral.base}
            style={styles.noEventsContainer}
        >
            <Handle handleColor={designSystem.colors.neutral500} handleContainerStyle={styles.handle} />
            <Text variant={'bodyMedium'} fontWeight={'bold'} marginTop={'s24'}>
                No events available.
            </Text>
        </Box>
    );
};

const styles = StyleSheet.create({
    noEventsContainer: {
        borderTopLeftRadius: SHEET_BORDER_RADIUS,
        borderTopRightRadius: SHEET_BORDER_RADIUS,
    },
    handle: {
        backgroundColor: 'transparent',
    },
});
